<template>
  <div class="agent-thread">
    <header class="agent-thread__head">
      <div class="agent-thread__title" :title="thread.summary.title">{{ thread.summary.title }}</div>
      <div class="agent-thread__controls">
        <label
          v-if="hasOtherBrands"
          class="agent-thread__switch"
          :class="{ 'agent-thread__switch--disabled': reachLocked }"
          :title="
            reachLocked
              ? 'Wait for the agent to finish before changing which brands it reaches'
              : allBrands
                ? 'The agent reads and writes every brand you have access to, and the thread shows whichever brand is open'
                : `The agent only reaches ${brandName}. Turn on to let it read and write every brand you have access to`
          "
        >
          <fs-switch :model-value="allBrands" size="tiny" :disabled="reachLocked" @update:model-value="setAllBrands" />
          <span>All brands</span>
        </label>
        <label class="agent-thread__switch" title="Run writes without asking first">
          <fs-switch :model-value="thread.summary.autoApproveWrites" size="tiny" @update:model-value="setAutoApprove" />
          <span>Auto-approve writes</span>
        </label>
        <fs-button v-if="agentChat.isBusy" size="small" secondary :loading="stopping" @click="stop">Stop</fs-button>
        <span v-if="thread.summary.isArchived" class="agent-thread__archived">Archived</span>
      </div>
    </header>

    <div ref="transcriptRef" class="agent-thread__transcript" @scroll="onScroll">
      <div v-if="!thread.messages.length" class="agent-thread__empty">
        <div class="agent-thread__empty-title">What should the agent look into?</div>
        <p>
          Ask it to analyse your data, hand it a spreadsheet or a PDF to work from, or tell it what to change. It asks
          you before changing anything.
        </p>
      </div>

      <template v-for="row in rows" :key="row.key">
        <agent-tool-group v-if="row.tools" :messages="row.messages" />
        <agent-message v-else :message="row.messages[0]" />
      </template>

      <div v-for="(draft, eventId) in agentChat.drafts" :key="eventId" class="agent-thread__draft">
        <agent-markdown :source="draft" />
        <span class="agent-thread__cursor" />
      </div>

      <div v-if="showWorking" class="agent-thread__working">
        <span class="agent-thread__dot" /><span class="agent-thread__dot" /><span class="agent-thread__dot" />
      </div>
    </div>

    <agent-composer
      ref="composerRef"
      :key="thread.summary.id"
      :thread-id="thread.summary.id"
      :busy="agentChat.isBusy"
      :read-only="thread.summary.isArchived"
      @sent="scrollToBottom"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import type { AgentMessageDto, AgentThreadDto } from '@/ApiClient';
import { AgentMessageRole, AgentThreadStatus } from '@/ApiClient';
import { useAgentChat } from '@/store/agent-chat';
import { useUser } from '@/store/user';
import { usePlmBrands } from '@lib/brand-switch';
import FsSwitch from '@components/form-elements/fs-switch.vue';
import AgentComposer from './agent-composer.vue';
import AgentMarkdown from './agent-markdown.vue';
import AgentMessage from './agent-message.vue';
import AgentToolGroup from './agent-tool-group.vue';
import FsButton from '@components/buttons/fs-button.vue';

const { thread } = defineProps<{ thread: AgentThreadDto }>();

const agentChat = useAgentChat();
const transcriptRef = ref<HTMLElement>();
const composerRef = ref<InstanceType<typeof AgentComposer>>();
const stopping = ref(false);

/**
 * The transcript, with a run of tool calls folded into one row.
 *
 * A turn that reads twenty things puts twenty rows between the question and the answer, and none
 * of them is what the user came for. Consecutive calls collapse to a line naming what ran;
 * anything the agent actually said stays where it is.
 */
const rows = computed(() => {
  const out: { key: string; tools: boolean; messages: AgentMessageDto[] }[] = [];
  for (const message of thread.messages) {
    const last = out[out.length - 1];
    if (message.role === AgentMessageRole.Tool && last?.tools) last.messages.push(message);
    else out.push({ key: `${message.id}`, tools: message.role === AgentMessageRole.Tool, messages: [message] });
  }
  return out;
});

// The dots show while the agent is between visible steps: thinking, or a tool call that has
// not answered yet. Once text streams, the draft itself is the sign of life.
const showWorking = computed(
  () => thread.summary.status === AgentThreadStatus.Running && Object.keys(agentChat.drafts).length === 0
);

const setAutoApprove = (value: boolean) => agentChat.update({ autoApproveWrites: value });

// A thread starts locked to the brand it was made in. Letting it reach the rest is only a choice
// for someone who has a rest; turning it back off locks it to the brand open now.
const plmBrands = usePlmBrands();
const hasOtherBrands = computed(() => plmBrands.value.length > 1);
const allBrands = computed(() => thread.summary.lockedBrandId == null);
const brandName = computed(() => useUser().company?.name ?? 'this brand');
// The turn in flight keeps the reach it started with, so the server refuses a change until it ends.
const reachLocked = computed(() => thread.summary.status === AgentThreadStatus.Running);
const setAllBrands = (value: boolean) => agentChat.update({ lockedBrandId: value ? undefined : useUser().brandId });

const stop = async () => {
  stopping.value = true;
  try {
    await agentChat.cancel();
  } finally {
    stopping.value = false;
  }
};

// Follows new content unless the user has scrolled up to read something.
const scrolledUp = ref(false);
const onScroll = () => {
  const el = transcriptRef.value;
  if (!el) return;
  scrolledUp.value = el.scrollHeight - el.clientHeight - el.scrollTop > 160;
};
const scrollToBottom = () => {
  nextTick(() => {
    const el = transcriptRef.value;
    if (el) el.scrollTop = el.scrollHeight;
    scrolledUp.value = false;
  });
};

watch(
  () => [thread.messages.length, Object.values(agentChat.drafts).join('').length, showWorking.value],
  () => {
    if (!scrolledUp.value) scrollToBottom();
  }
);
watch(
  () => thread.summary.id,
  () => {
    scrolledUp.value = false;
    scrollToBottom();
  }
);

onMounted(() => {
  scrollToBottom();
  composerRef.value?.focus();
});
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  &__head {
    flex: none;
    display: flex;
    align-items: center;
    gap: 1.6rem;
    padding: 1.2rem 2rem;
    border-bottom: 1px solid $c-hairline;
  }

  &__title {
    flex: 1;
    min-width: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__controls {
    flex: none;
    display: flex;
    align-items: center;
    gap: 1.6rem;
  }

  &__switch {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.2rem;
    color: $c-ink-soft;
    cursor: pointer;

    &--disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  &__transcript {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 1.4rem;
    padding: 2rem;
  }

  &__empty {
    margin: auto;
    max-width: 42rem;
    text-align: center;
    font-size: 1.4rem;
    line-height: 2.2rem;
    color: rgba($c-ink, 0.5);

    p {
      margin: 0;
    }
  }

  &__empty-title {
    margin-bottom: 0.8rem;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.6rem;
    font-weight: 600;
    color: $c-ink;
  }

  &__archived {
    padding: 0.3rem 1rem;
    border-radius: $br-pill;
    background: $c-surface-muted;
    font-size: 1.1rem;
    color: $c-ink-soft;
  }

  &__draft {
    position: relative;
  }

  &__cursor {
    display: inline-block;
    width: 0.6rem;
    height: 1.6rem;
    margin-left: 0.2rem;
    vertical-align: text-bottom;
    background: $c-ink;
    animation: agent-thread-blink 1s steps(2) infinite;
  }

  &__working {
    display: flex;
    gap: 0.5rem;
    padding: 0.4rem 0;
  }

  &__dot {
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: rgba($c-ink, 0.35);
    animation: agent-thread-bounce 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }
    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

@keyframes agent-thread-blink {
  to {
    visibility: hidden;
  }
}

@keyframes agent-thread-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-0.4rem);
    opacity: 1;
  }
}
</style>
